import { useFilters } from '@/setup/filters';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCrmStatus } from '@/views/CRM/composables/useCrmStatus';
import type { CrmActivity } from '@/types/crm';

type ConfirmIndexItemType = {
  title: string;
  msg: string;
  type: string;
  action: string;
  actionName: string;
  confirmAction: () => void;
};

type ConfirmIndexFactory = (activity: CrmActivity, confirmAction: () => void) => ConfirmIndexItemType;

export const useActivitiesCommon = () => {
  const filters = useFilters();
  const { t } = useI18n();
  const { detectCrmStatus, isCanBeArchived } = useCrmStatus();

  const getDefaultConfirm = (): ConfirmIndexItemType => ({
    title: '',
    msg: '',
    type: 'warning',
    action: 'delete',
    actionName: '',
    confirmAction: () => {},
  });

  const confirmIndex = computed(() => ({
    'save-and-send-to-review': (activity, confirmAction) => ({
      title: t('crm.confirm.save_and_to_review_activity.title'),
      msg: `${t('crm.confirm.save_and_to_review_activity.msg', { activityName: activity.name })}`,
      type: 'success',
      action: 'approve',
      actionName: `${t('crm.confirm.save_and_to_review_activity.action')}`,
      confirmAction,
    }),
    'send-to-review': (activity, confirmAction) => ({
      title: t('crm.confirm.to_review_activity.title'),
      msg: `${t('crm.confirm.to_review_activity.msg', { activityName: activity.name })}`,
      type: 'success',
      action: 'approve',
      actionName: `${t('crm.confirm.to_review_activity.action')}`,
      confirmAction,
    }),
    approve: (activity, confirmAction) => ({
      title: t('crm.confirm.approve_activity.title'),
      msg: `${t('crm.confirm.approve_activity.msg', { activityName: activity.name })}`,
      type: 'success',
      action: 'approve',
      actionName: `${t('crm.confirm.approve_activity.action')}`,
      confirmAction,
    }),
    'for-rework': (activity, confirmAction) => ({
      title: t('crm.confirm.for_rework_activity.title'),
      msg: `${t('crm.confirm.for_rework_activity.msg', { activityName: activity.name })}`,
      type: 'success',
      action: 'for-rework',
      actionName: `${t('crm.confirm.for_rework_activity.action')}`,
      confirmAction,
    }),
    decline: (activity, confirmAction) => ({
      title: t('crm.confirm.decline_activity.title'),
      msg: `${t('crm.confirm.decline_activity.msg', { activityName: activity.name })}`,
      type: 'warning',
      action: 'decline',
      actionName: `${t('crm.confirm.decline_activity.action')}`,
      confirmAction,
    }),
    archive: (activity, confirmAction) => ({
      title: t('crm.confirm.delete_activity.title'),
      msg: `${t('crm.confirm.delete_activity.msg', { activityName: activity.name })}`,
      type: 'warning',
      action: 'archive',
      actionName: `${t('crm.confirm.delete_activity.action')}`,
      confirmAction,
    }),
    run: (activity, confirmAction) => ({
      title: t('crm.confirm.run_activity.title'),
      msg: `${t('crm.confirm.run_activity.msg', {
        activityName: activity.name,
        dateTime: filters.formatDate3(activity.activity_period_start),
      })}`,
      type: 'success',
      action: 'run',
      actionName: `${t('crm.confirm.run_activity.action')}`,
      confirmAction,
    }),
    pause: (activity, confirmAction) => ({
      title: t('crm.confirm.pause_activity.title'),
      msg: `${t('crm.confirm.pause_activity.msg', { activityName: activity.name })}`,
      type: 'success',
      action: 'pause',
      actionName: `${t('crm.confirm.pause_activity.action')}`,
      confirmAction,
    }),
    complete: (activity, confirmAction) => ({
      title: t('crm.confirm.complete_activity.title'),
      msg: `${t('crm.confirm.complete_activity.msg', { activityName: activity.name })}`,
      type: 'warning',
      action: 'complete',
      actionName: `${t('crm.confirm.complete_activity.action')}`,
      confirmAction,
    }),
    clone: (activity, confirmAction) => ({
      title: t('crm.confirm.clone_activity.title'),
      msg: `${t('crm.confirm.clone_activity.msg', { activityName: activity.name })}`,
      type: 'success',
      action: 'complete',
      actionName: `${t('crm.confirm.clone_activity.action')}`,
      confirmAction,
    }),
  } satisfies Record<string, ConfirmIndexFactory>));

  return {
    detectCrmStatus,
    isCanBeArchived,
    getDefaultConfirm,
    confirmIndex,
  };
};
